import { concat } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismLoadingBar, PrismRadioGroup } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import type { Insightpagesfavourite } from 'frontend-utils/models';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import { FILTER_BAR_PAGETYPES } from 'lumen/utils/constants/general-constants';
import {
  MI_SETTINGS_RATE_PREFERENCE_FILTER_VALUE_BEST_FLEX,
  MI_SETTINGS_RATE_PREFERENCE_FILTER_VALUE_LOWEST,
  MI_SETTINGS_RATE_PREFERENCE_INSIGHTPAGESFAVOURITE_NAME,
} from 'lumen/utils/constants/market-insight-constants';

import styles from './rate-preference-modal.module.css';

interface HotelMarketInsightModalsRatePreferenceModalSignature {
  Args: {
    closeModal: unknown;
    doRefresh: unknown;
  };
}

export default class HotelMarketInsightModalsRatePreferenceModal extends Component<HotelMarketInsightModalsRatePreferenceModalSignature> {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare store: Services['store'];

  // Will be used to store Rate preference value after opening the modal - (default value is best flex)
  @tracked currentRatePreferenceValue =
    MI_SETTINGS_RATE_PREFERENCE_FILTER_VALUE_BEST_FLEX;
  // Will be filled in case the current hotel already has a rate preference insightpagesfavourites object
  @tracked existingRatePrefInsightPagesFavourite = null;
  @tracked isRequestingInsightPagesFavourites = true;

  // Will be used to store Rate preference value in the modal (tied to the radio buttons) - (default value is best flex)
  @tracked updatedRatePreferenceValue =
    MI_SETTINGS_RATE_PREFERENCE_FILTER_VALUE_BEST_FLEX;

  createAndSaveInsightPagesFavouriteTask = task(
    {},
    async (updatedRatePreferenceValue) => {
      // Used when there is no existing insightPagesFavourite. Create a new record and persist this.

      const insightPagesFavourite =
        this.store.createRecord<Insightpagesfavourite>(
          'insightpagesfavourite',
          {
            name: MI_SETTINGS_RATE_PREFERENCE_INSIGHTPAGESFAVOURITE_NAME,
            filters: {
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              rate_preference: updatedRatePreferenceValue,
            },
            pageType: FILTER_BAR_PAGETYPES.SETTINGS_PAGE,
            // @ts-expect-error: Incorrect type
            hotel: this.hotelAndMarketService.hotel,
          },
        );

      try {
        const response = await insightPagesFavourite.save();

        if (!response.errors.length) {
          // @ts-expect-error: Incorrect type
          this.args.closeModal();
          // @ts-expect-error: Incorrect type
          this.args.doRefresh();
        }
      } catch (e) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert();

        this.errorTracking.captureException(e);
      } finally {
        if (insightPagesFavourite.isError) {
          // Make sure that if there was in any case an error, to delete the record
          insightPagesFavourite.deleteRecord();
        }
      }
    },
  );

  requestInsightPagesFavouritesTask = task({}, async () => {
    const requestFavourites = {
      // @ts-expect-error: Incorrect type
      hotel: this.hotelAndMarketService.hotel.id,
      page_type: FILTER_BAR_PAGETYPES.SETTINGS_PAGE,
    };

    const insightPagesFavouritesResponse =
      await this.store.query<Insightpagesfavourite>(
        'insightpagesfavourite',
        requestFavourites,
      );

    const ratePrefInsightPagesFavourites =
      insightPagesFavouritesResponse.filter(
        (insightPagesFavourite) =>
          insightPagesFavourite.name ===
          MI_SETTINGS_RATE_PREFERENCE_INSIGHTPAGESFAVOURITE_NAME,
      );

    if (ratePrefInsightPagesFavourites.length) {
      // @ts-expect-error: Incorrect type
      this.existingRatePrefInsightPagesFavourite =
        ratePrefInsightPagesFavourites[0];

      // Set the correct rate preference value if there is one
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.currentRatePreferenceValue =
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.existingRatePrefInsightPagesFavourite.filters.rate_preference;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.updatedRatePreferenceValue =
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.existingRatePrefInsightPagesFavourite.filters.rate_preference;
    }

    this.isRequestingInsightPagesFavourites = false;
  });

  updateExistingInsightPagesFavouriteTask = task(
    {},
    async (existingInsightPagesFavourite, updatedRatePreferenceValue) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        existingInsightPagesFavourite.filters = {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          rate_preference: updatedRatePreferenceValue,
        };
        // Make sure we use the pageType to 'SE' for these filters
        // Required for now since we orginally used 'MI', but this needs to be phased out
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        existingInsightPagesFavourite.pageType =
          FILTER_BAR_PAGETYPES.SETTINGS_PAGE;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        const response = await existingInsightPagesFavourite.save();

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (!response.errors.length) {
          // @ts-expect-error: Incorrect type
          this.args.closeModal();
          // @ts-expect-error: Incorrect type
          this.args.doRefresh();
        }
      } catch (e) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        existingInsightPagesFavourite.rollbackAttributes();

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert();

        this.errorTracking.captureException(e);
      }
    },
  );

  constructor() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.requestInsightPagesFavouritesTask.perform();
  }

  @action
  applyChanges(): void {
    if (
      this.updatedRatePreferenceValue &&
      this.updatedRatePreferenceValue !== this.currentRatePreferenceValue
    ) {
      // In this case it means the user has changed his rate preference value. We need to save this!
      if (this.existingRatePrefInsightPagesFavourite === null) {
        // case 1: no existing insightpagesfavourite
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.createAndSaveInsightPagesFavouriteTask.perform(
          this.updatedRatePreferenceValue,
        );
      } else {
        // case 2: existing insightspagesfavourite
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.updateExistingInsightPagesFavouriteTask.perform(
          this.existingRatePrefInsightPagesFavourite,
          this.updatedRatePreferenceValue,
        );
      }
    } else {
      // No changes: just close the modal
      // @ts-expect-error: Incorrect type
      this.args.closeModal();
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  setUpdatedRatePreferenceValue(event): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    this.updatedRatePreferenceValue = event.target.value;
  }

  <template>
    <div class={{concat "modal--small modal--padded" " " styles.container}}>
      {{#if this.isRequestingInsightPagesFavourites}}
        <PrismLoadingBar />
      {{else}}
        <h4 class={{styles.title}}>
          {{t "market_insight.settings.rate_preference.explanation_intro"}}
        </h4>
        <p class={{concat styles.description " " "paragraph-300--regular"}}>
          {{t "market_insight.settings.rate_preference.explanation_text"}}
        </p>
        <PrismRadioGroup
          @isHorizontal={{true}}
          @name="rate-preference-radio-group"
          @onChange={{this.setUpdatedRatePreferenceValue}}
          @value={{this.updatedRatePreferenceValue}}
          as |RadioGroup|
        >
          <RadioGroup.input
            @value={{MI_SETTINGS_RATE_PREFERENCE_FILTER_VALUE_BEST_FLEX}}
          >
            {{t "rates.best_flex"}}
          </RadioGroup.input>
          <RadioGroup.input
            @value={{MI_SETTINGS_RATE_PREFERENCE_FILTER_VALUE_LOWEST}}
          >
            {{t "rates.lowest"}}
          </RadioGroup.input>
        </PrismRadioGroup>
      {{/if}}
    </div>
    <div class={{styles.footer}}>
      <GeneralFormFieldsCancelAndConfirm
        {{! @glint-expect-error: Incorrect type }}
        @cancel={{@closeModal}}
        @confirm={{this.applyChanges}}
      />
    </div>
  </template>
}
